'use client';

import type { JobResult } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/JobResult';
import type { PremiseResult } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/PremiseResult';
import type { SearchResults } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/SearchResults';
import { getPremiseAddress } from '@dte/otw-azure-functions/src/common/premises/data/attributes/address/premiseAddress';
import { lowerCaseValue } from '@dte/otw-utils-core/src/strings/lowerCaseValue';
import { stableId } from '@dte/otw-utils-core/src/strings/stableId';
import { titleCaseValue } from '@dte/otw-utils-core/src/strings/titleCaseValue';
import { upperCaseValue } from '@dte/otw-utils-core/src/strings/upperCaseValue';
import { faMagnifyingGlass, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ActionIcon, Combobox, Grid, TextInput, useCombobox, VisuallyHidden } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import NextForm from 'next/form';
import { useRouter } from 'next/navigation';
import { type ReactElement, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import { mutate } from 'swr';
import { AuthenticatedJsonContent } from '../../../app/(client)/(authenticated)/content/jsonGet/AuthenticatedJsonContent';
import { PremiseMarker } from '../../../app/(client)/(authenticated)/premises/search/PremiseMarker';
import { AppAction } from '../../../contexts/TokenContext/Roles';
import { useIsAllowed } from '../../../contexts/TokenContext/useIsAllowed';
import { useTriggerOnPathChange } from '../../../hooks/usePathChanged';
import { Icon } from '../../Spinner/Icon';
import { formatLongDateTime } from '../../Tables/formatShortDateTime';
import { getSearchURL } from './getSearchURL';
import styles from './SearchBar.module.scss';

function refresh(): void {
	const path = '/authenticated/data';
	void mutate((key) => typeof key === 'string' && key.includes(path), undefined, {
		revalidate: true,
	});
}

interface SearchProps {
	queryInput: string;
	setQueryInput: (value: string) => void;
	openDropdown: () => void;
	closeDropdown: () => void;
	selectFirstOption: () => void;
	loading?: boolean;
}

function SearchInput(props: SearchProps): ReactNode {
	const queryInput = props.queryInput ?? '';
	const setQueryInput = props.setQueryInput;
	const inputRef = useRef<HTMLInputElement>(null);

	const changeQueryInput = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setQueryInput(value);
			props.openDropdown();
			props.selectFirstOption();
		},
		[props, setQueryInput],
	);

	const clearQueryInput = useCallback(() => {
		setQueryInput('');
		props.openDropdown();
		props.selectFirstOption();
		inputRef.current?.focus();
	}, [props, setQueryInput]);

	let placeholder = 'Search for Premise or Job';
	if (queryInput) {
		placeholder = '';
	}

	const searchInputId = useId();

	const searchButton = (
		<ActionIcon
			name="search"
			type="submit"
			className={styles['iconButton']}
			title={props.loading ? 'Loading...' : 'Search'}
			disabled={props.loading}
		>
			<Icon icon={props.loading ? faSpinner : faMagnifyingGlass} spin={props.loading} />
		</ActionIcon>
	);

	const clearButton = queryInput ? (
		<ActionIcon
			className={styles['iconButton']}
			onMouseDown={(event) => event.preventDefault()}
			onClick={clearQueryInput}
			aria-label="Clear search criteria"
			title="Clear search criteria"
		>
			<Icon icon={faXmark} />
		</ActionIcon>
	) : undefined;

	return (
		<>
			<VisuallyHidden>
				<label htmlFor={searchInputId}>Search</label>
			</VisuallyHidden>
			<TextInput
				ref={inputRef}
				id={searchInputId}
				leftSectionPointerEvents="none"
				leftSection={searchButton}
				rightSectionPointerEvents="auto"
				rightSection={clearButton}
				value={queryInput}
				onChange={changeQueryInput}
				placeholder={placeholder}
				autoCapitalize="off"
				autoCorrect="off"
				autoComplete="off"
				spellCheck={false}
				onFocus={props.openDropdown}
				onBlur={props.closeDropdown}
				className={styles['searchInput']}
			/>
		</>
	);
}

interface SearchResultsProps {
	queryInput: string;
	searchResults: SearchResults | undefined;
	loading?: boolean;
}

type SearchOptionValue = `premise:${string}` | `job:${string}`;

function longestTokenMatch(value: string, query: string | undefined): string | undefined {
	const lowerValue = lowerCaseValue(value);
	if (!lowerValue) {
		return undefined;
	}

	const tokens = query?.split(' ') ?? [];
	let longestMatch: string | undefined;

	for (const token of tokens) {
		// Do case insensitive matching
		const lowerToken = lowerCaseValue(token);

		// Only looking for things that start with the token
		if (!lowerToken) {
			continue;
		}
		if (!lowerValue.startsWith(lowerToken)) {
			continue;
		}

		// First match
		if (!longestMatch) {
			longestMatch = token;
		}

		// This token is longer than previous matches
		if (token.length > longestMatch.length) {
			longestMatch = token;
		}
	}

	return longestMatch;
}

interface HighlightedPartsProps {
	value: string | undefined;
	query: string | undefined;
	titleize?: boolean;
}

function HighlightedParts(props: HighlightedPartsProps): ReactNode {
	let value: string | undefined = props.value;
	if (props.titleize) {
		value = titleCaseValue(value);
	}

	if (!value) {
		return [<span key={0}>&nbsp;</span>];
	}

	const query = props.query;

	const valueParts = value.split(' ');

	const keyCount: Record<string, number> = {};
	const highlightedParts: ReactElement[] = valueParts.map((part, index) => {
		const token = longestTokenMatch(part, query);
		const tokenLength = token?.length;

		let ending: ReactNode = ' ';
		if (index === valueParts.length - 1) {
			ending = null;
		}

		// Get a consistent key
		const baseKey = stableId(part);
		// Add an incrementing suffix to the key
		let currentCount = keyCount[baseKey] ?? 1;
		const key = `${baseKey}-${currentCount}`;
		currentCount++;
		keyCount[baseKey] = currentCount;

		// No tokens matched
		if (!tokenLength) {
			return (
				<span key={key}>
					{part}
					{ending}
				</span>
			);
		}

		// Got a match
		const before = part.substring(0, tokenLength);
		const after = part.substring(tokenLength);
		return (
			<span key={key}>
				<b>{before}</b>
				{after}
				{ending}
			</span>
		);
	});

	return highlightedParts;
}

function shouldShowLine2(searchResults: SearchResults | undefined): boolean {
	const premises = searchResults?.premises;
	if (!premises) {
		return false;
	}

	const showLine2 = premises.find((findValue) => getPremiseAddress(findValue?.premiseData)?.line2);
	return showLine2 !== undefined;
}

interface PremiseResultOptionProps {
	premiseResult: PremiseResult;
	queryInput: string;
	showLine2: boolean;
}

function PremiseResultOption(props: PremiseResultOptionProps): ReactNode {
	const premiseResult = props.premiseResult;
	const premiseData = premiseResult.premiseData;
	const address = getPremiseAddress(premiseData);
	if (!address) {
		return null;
	}

	const value: SearchOptionValue = `premise:${premiseResult.premiseId}`;

	return (
		<Combobox.Option value={value}>
			<Grid className={styles['optionRow']} gap="xs" align="flex-start">
				<Grid.Col span="content" className={styles['marker']}>
					<PremiseMarker premiseData={premiseData} size="small">
						&nbsp;
					</PremiseMarker>
				</Grid.Col>
				<Grid.Col span="auto" className={styles['optionContent']}>
					<Grid className={styles['premiseDetailsGrid']} gap="xs" align="center" columns={12}>
						<Grid.Col span="content" className={styles['addressLineDesktop']}>
							<HighlightedParts query={props.queryInput} value={address.line1} />
						</Grid.Col>
						{props.showLine2 ? (
							<Grid.Col span="content" className={styles['addressLine2Desktop']}>
								<HighlightedParts query={props.queryInput} value={address.line2} titleize={true} />
							</Grid.Col>
						) : (
							<Grid.Col span="content" className={styles['addressLine2Placeholder']} aria-hidden="true">
								&nbsp;
							</Grid.Col>
						)}
						<Grid.Col span={12} className={styles['addressGroupMobile']}>
							<div className={styles['addressLine']}>
								<HighlightedParts query={props.queryInput} value={address.line1} />
							</div>
							{props.showLine2 ? (
								<div className={styles['addressLine2']}>
									<HighlightedParts query={props.queryInput} value={address.line2} titleize={true} />
								</div>
							) : null}
						</Grid.Col>
						<Grid.Col span="content" className={styles['cityDesktop']}>
							<HighlightedParts query={props.queryInput} value={address.city} />
						</Grid.Col>
						<Grid.Col span="content" className={styles['stateDesktop']}>
							<HighlightedParts query={props.queryInput} value={address.state} />
						</Grid.Col>
						<Grid.Col span={12} className={styles['cityStateMobile']}>
							<HighlightedParts query={props.queryInput} value={address.city} />,{' '}
							<HighlightedParts query={props.queryInput} value={address.state} />
						</Grid.Col>
					</Grid>
				</Grid.Col>
			</Grid>
		</Combobox.Option>
	);
}

function JobResultOption(props: { jobResult: JobResult; queryInput: string }): ReactNode {
	const jobResult = props.jobResult;
	const jobStatus = jobResult.jobStatus;
	if (!jobStatus) {
		return null;
	}

	const value: SearchOptionValue = `job:${jobResult.jobId}`;
	const jobDisplayId = upperCaseValue(jobStatus?.data?.jobDisplayId);
	const created = jobStatus?.data?.jobCreateTime;

	return (
		<Combobox.Option value={value} className={styles['option']}>
			<div className={styles['optionContent']}>
				<div>
					<HighlightedParts query={props.queryInput} value={jobDisplayId} />
				</div>
				<Grid className={styles['jobDetailsGrid']} gap="xs" align="center" columns={12}>
					<Grid.Col span="content" className={styles['jobTypeDesktop']}>
						{titleCaseValue(jobStatus?.data?.jobType)}
					</Grid.Col>
					<Grid.Col span="auto" className={styles['optionSubline']}>
						{formatLongDateTime(created)}
					</Grid.Col>
				</Grid>
			</div>
		</Combobox.Option>
	);
}

function getJobSearchOptions(searchResults: SearchResults | undefined, queryInput: string): ReactElement[] {
	let jobs = searchResults?.jobs ?? [];
	jobs = jobs.slice(0, 10);

	const options: ReactElement[] = [];
	for (const job of jobs) {
		const option = <JobResultOption key={job.jobId} queryInput={queryInput} jobResult={job} />;
		if (option) {
			options.push(option);
		}
	}

	return options;
}

function getPremiseSearchOptions(searchResults: SearchResults | undefined, queryInput: string): ReactElement[] {
	let premises = searchResults?.premises ?? [];
	premises = premises.slice(0, 10);
	const showLine2 = shouldShowLine2(searchResults);

	const options: ReactElement[] = [];
	for (const premise of premises) {
		const option = (
			<PremiseResultOption
				key={premise.premiseId}
				queryInput={queryInput}
				premiseResult={premise}
				showLine2={showLine2}
			/>
		);
		if (option) {
			options.push(option);
		}
	}

	return options;
}

function SearchResultsContent(props: SearchResultsProps): ReactNode {
	const queryInput = props.queryInput;
	const searchResults = props.searchResults;
	const hasQueryInput = Boolean(queryInput?.trim());

	if (!hasQueryInput) {
		return (
			<Combobox.Empty className={styles['emptyState']}>
				Enter an address or id to search:
				<ul className={styles['helpList']}>
					<li>Premise</li>
					<li>Call</li>
					<li>Job</li>
					<li>Order</li>
					<li>Meter</li>
				</ul>
			</Combobox.Empty>
		);
	}

	const premiseOptions = getPremiseSearchOptions(searchResults, queryInput);
	if (premiseOptions.length > 0) {
		return <Combobox.Group label="Premises">{premiseOptions}</Combobox.Group>;
	}

	const jobOptions = getJobSearchOptions(searchResults, queryInput);
	if (jobOptions.length > 0) {
		return <Combobox.Group label="Jobs">{jobOptions}</Combobox.Group>;
	}

	const loading = props.loading;
	if (loading) {
		return <Combobox.Empty className={styles['emptyState']}>Loading...</Combobox.Empty>;
	}

	if (queryInput) {
		return <Combobox.Empty className={styles['emptyState']}>No matches</Combobox.Empty>;
	}

	return null;
}

export function SearchBar(): ReactNode {
	const [queryInput, setQueryInput] = useState<string>('');
	const hasAccess = useIsAllowed(AppAction.SearchOrders);
	const router = useRouter();

	const [debouncedQueryInput] = useDebouncedValue(queryInput, 100);
	const url = getSearchURL(debouncedQueryInput);

	const [searchResults, setSearchResults] = useState<SearchResults>();
	const [loading, setLoading] = useState(false);
	const combobox = useCombobox();

	// Hide the results if we select something
	const changePath = useCallback(() => {
		// Clear the query
		setQueryInput('');

		// Hide the dropdown
		combobox.closeDropdown();
	}, [combobox]);
	useTriggerOnPathChange(changePath);

	const selectFirstOption = useCallback(() => {
		combobox.selectFirstOption();
	}, [combobox]);

	const onOptionSubmit = useCallback(
		(value: string) => {
			if (value.startsWith('premise:')) {
				const premiseId = value.substring('premise:'.length);
				router.push(`/premises/?premiseId=${premiseId}`);
			} else if (value.startsWith('job:')) {
				const jobId = value.substring('job:'.length);
				router.push(`/jobs/?jobId=${jobId}`);
			}

			setQueryInput('');
			combobox.closeDropdown();
		},
		[combobox, router],
	);

	const search = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			if (!loading) {
				// On manually pressing search, clear caches and refresh
				refresh();
			}
			// Call prevent default becase we don't want to submit the form and cause a page refresh
			event.preventDefault();
		},
		[loading],
	);

	// Hide search widget unless we know that the user has access
	if (!hasAccess) {
		return null;
	}

	return (
		<NextForm action="" onSubmit={search}>
			<Combobox
				store={combobox}
				onOptionSubmit={onOptionSubmit}
				withinPortal={false}
				classNames={{ dropdown: styles['selectionList'] }}
			>
				<Combobox.Target>
					<div className={styles['inputColumn']}>
						<SearchInput
							queryInput={queryInput}
							setQueryInput={setQueryInput}
							openDropdown={combobox.openDropdown}
							closeDropdown={combobox.closeDropdown}
							selectFirstOption={selectFirstOption}
							loading={loading}
						/>
					</div>
				</Combobox.Target>
				<Combobox.Dropdown>
					<Combobox.Options>
						<SearchResultsContent queryInput={queryInput} searchResults={searchResults} loading={loading} />
					</Combobox.Options>
				</Combobox.Dropdown>
			</Combobox>
			<AuthenticatedJsonContent
				url={url}
				setData={setSearchResults}
				setLoading={setLoading}
				loadingContent={null}
			/>
		</NextForm>
	);
}
